import Statistic from "common/Statistic/Statistic";
import BotChange from "features/bots/common/BotChange";
import React from "react";
import styled, { useTheme } from "styled-components";
import StatStrip, { StatStripItem } from "common/NewLook/StatStrip.tsx";
import { BotData, BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { isGridPnlBotType } from "@shared/features/bots/utils";

interface BotGeneralStatisticsProps {
  botData?: BotData;
}

const BotGeneralStatistics = ({ botData }: BotGeneralStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isHedgingBot = botData?.bot_type === BotType.HEDGING;
  const useGridPnl = !!botData && isGridPnlBotType(botData.bot_type);
  const isDcaBot = botData?.bot_type === BotType.ASAP;
  const { isNewLook } = useTheme();

  if (isNewLook) {
    // Flat key figures: profit leads (larger), the rest follow; the days the
    // bot has been running are a quiet line under the lead figure.
    const days = botData?.days_running;
    const daysMeta =
      (useGridPnl || isDcaBot) && days !== undefined && days !== null
        ? Number(days) === 1
          ? t("labels.running_for_one", { count: 1 })
          : t("labels.running_for_other", { count: Number(days) })
        : undefined;
    const cells: StatStripItem[] = useGridPnl
      ? [
          {
            key: "pnl",
            label: t("labels.realized_pnl"),
            value: <BotChange botData={botData} isRealizedPnl={true} />,
            emphasis: true,
            meta: daysMeta,
          },
          {
            key: "actions",
            label: isHedgingBot
              ? t("labels.rebalances")
              : t("labels.take_profits_filled"),
            value: isHedgingBot
              ? botData?.number_of_rebalances
              : botData?.take_profits_filled,
          },
        ]
      : [
          {
            key: "profit",
            label: t("labels.profit"),
            value: <BotChange botData={botData} />,
            emphasis: true,
            meta: daysMeta,
          },
          {
            key: "closed",
            label: t("labels.closed_trades"),
            value: botData?.number_of_closed_trades,
          },
          {
            key: "open",
            label: t("labels.open_trades"),
            value: botData?.number_of_open_trades,
          },
        ];
    return (
      <StatStrip
        variant="flat"
        items={cells.map((cell) => ({ ...cell, value: cell.value ?? "\u2014" }))}
      />
    );
  }

  const getGridPnlStatistics = () => {
    if (!useGridPnl) return;
    return (
      <>
        <Statistic
          title={
            isHedgingBot
              ? t("labels.rebalances")
              : t("labels.take_profits_filled")
          }
          value={
            isHedgingBot
              ? botData?.number_of_rebalances
              : botData?.take_profits_filled
          }
        />
        <Statistic
          title={t("labels.realized_pnl")}
          value={
            <BotChange
              botData={botData}
              isRealizedPnl={true}
              style={{ marginTop: 4 }}
            />
          }
        />
      </>
    );
  };

  return (
    <Wrapper>
      {getGridPnlStatistics()}
      {!useGridPnl && (
        <>
          <Statistic
            title={t("labels.closed_trades")}
            value={
              <div style={{ display: "flex", marginTop: 4 }}>
                <span style={{ marginRight: 4 }}>
                  {botData?.number_of_closed_trades}
                </span>{" "}
                <BotChange botData={botData} />
              </div>
            }
          />
          <Statistic
            title={t("labels.open_trades")}
            value={botData?.number_of_open_trades}
          />
        </>
      )}
      {(useGridPnl || isDcaBot) && (
        <Statistic
          title={t("labels.days_running")}
          value={botData?.days_running}
        />
      )}
    </Wrapper>
  );
};

export default BotGeneralStatistics;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 50px;
`;
